import React from "react"
import Chip from "../atoms/misc/Chip.atom"
import Tooltip from "../atoms/Tooltip.atom"
import useClientColorsContext from "../../hooks/contextConsumers/useClientColorsContext.hook"
import {
  backwardCompatibleClientProjectLabel,
  formatToHtmlId,
} from "../../utils/strings.utils"
import PropTypes from "prop-types"
import { dbg } from "../../utils/general.utils"

/**
 * @typedef {JSX.IntrinsicAttributes & JSX.IntrinsicElements['div']} JSXDivElement
 * @typedef {import("prop-types").InferProps<typeof propTypes>} PropTypes
 */

/**
 * @param {JSXDivElement & PropTypes} props
 * @returns
 */
const ClientProjectChip = ({
  clientCode,
  projectCode,
  onClientClick,
  onProjectClick,
  labelClassname = "my-0 white-space-nowrap",
  className = "",
  showTooltip = true,
  tooltipPosition = "top",
  style,
  ...props
}) => {
  if (!clientCode || !projectCode) return null
  const { colors } = useClientColorsContext()
  const client = colors.find((client) => client.code === clientCode)
  const project = client?.projects.find(
    (project) => project.code === projectCode
  )

  dbg(() => {
    if (colors.length !== 0) {
      console.assert(
        client,
        `Client "${clientCode}" not found in colors %o`,
        colors
      )
      console.assert(
        project,
        `Project "${projectCode}" not found in client "${clientCode}"`
      )
    }
  })

  const randomId = formatToHtmlId(
    `${clientCode}-${projectCode}-${Math.random().toString(36).substring(7)}`
  )
  return (
    <>
      <Chip
        className={`client-project-chip overflow-hidden ${className} ${randomId}`}
        style={{
          ...style,
          "--chip-color": client?.color,
          color: "var(--text-color__dark)",
        }}
        template={
          <>
            <div
              className="client flex-1 flex align-items-center justify-content-center"
              style={{
                padding: "0 6px",
                width: "50%",
              }}
              onClick={onClientClick}
            >
              <div
                className={`${labelClassname} m-0 ${clientCode?.length > 3 && "overflow-hidden text-overflow-ellipsis"}`}
                style={{ fontSize: "12px", maxWidth: "4ch" }}
                title={clientCode?.length > 3 ? clientCode : ""}
              >
                {backwardCompatibleClientProjectLabel(clientCode)}
              </div>
            </div>
            <div
              className="project flex-1 flex align-items-center justify-content-center"
              style={{
                padding: "0 6px",
                backgroundColor:
                  "light-dark(var(--surface-highlight__light), transparent)",
                width: "50%",
              }}
              onClick={onProjectClick}
            >
              <div
                className={`${labelClassname} m-0 ${projectCode?.length > 3 && "overflow-hidden text-overflow-ellipsis"}`}
                style={{ fontSize: "12px", maxWidth: "4ch" }}
              >
                {backwardCompatibleClientProjectLabel(projectCode)}
              </div>
            </div>
          </>
        }
        {...props}
      />
      <Tooltip
        target={`.${randomId}`}
        position={tooltipPosition}
        style={{
          display: showTooltip ? "block" : "none",
        }}
      >
        <span className="font-bold">{client?.name}</span>:
        <span>{project?.name}</span>
      </Tooltip>
    </>
  )
}

const propTypes = {
  clientCode: PropTypes.string,
  projectCode: PropTypes.string,
  onClientClick: PropTypes.func,
  onProjectClick: PropTypes.func,
  labelClassname: PropTypes.string,
  className: PropTypes.string,
  showTooltip: PropTypes.bool,
  tooltipPosition: PropTypes.string,
  style: PropTypes.object,
}

ClientProjectChip.propTypes = propTypes

export default ClientProjectChip
